import { Stack } from '@mui/material';
import { useMemo, useState } from 'react';
import { SidePanelTabs } from 'src/components';
import { BlockContainer } from 'src/components/Layout/named-layout-block.view';
import { PartnerInfo } from 'src/components/SidePanelTabs/PartnerTab/partner-info';
import { PartnerInfoTabs } from 'src/components/SidePanelTabs/PartnerTab/partner-info-tabs';
import { useProjectQuickActions } from 'src/components/SidePanelTabs/Projects/use-project-quick-actions';
import { Project, useGroupRules, UserGroupRules } from 'src/graphql';
import { PartnerHeader } from 'src/pages/Conversations/SidePanel/partner-header';
import { ProjectCard } from 'src/pages/Projects/project-card';
import { useGlobalStore } from 'src/store';

const CLOSE_DETAILS_PANEL_TOOLTIP = 'Close Project Details';

enum ProjectDetailsTabs {
  Project = 'project',
  Partner = 'partner',
}

interface TabContentProps {
  project: Project;
  activeTab: ProjectDetailsTabs;
  onClose?: () => void;
}

interface ProjectSidePanelProps {
  project: Project;
  onClose: () => void;
}

const TabContent = ({ activeTab, project, onClose }: TabContentProps) => {
  const { address } = project;
  if (activeTab === ProjectDetailsTabs.Project && project) {
    return <ProjectCard project={project} variant="fullWidth" onDelete={onClose} />;
  }

  if (activeTab === ProjectDetailsTabs.Partner) {
    return (
      <Stack spacing={2}>
        <PartnerInfo partner={project.partner!} />
        <PartnerInfoTabs partner={project.partner!} address={address} />
      </Stack>
    );
  }

  return null;
};

export const ProjectSidePanel = ({ onClose, project }: ProjectSidePanelProps) => {
  const isMultiTenancyMode = useGlobalStore.use.isMultiTenancyMode();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const [activeTab, setActiveTab] = useState(ProjectDetailsTabs.Project);
  const { groupRules } = useGroupRules();
  const canAccessToPartnerTab = !!groupRules[UserGroupRules.AccessToPartnerTab];
  const canSeePartnersTab =
    canAccessToPartnerTab || (isMultiTenancyMode && selectedPartnerId === null);

  useProjectQuickActions(project);

  const tabs = useMemo(
    () =>
      [
        {
          value: ProjectDetailsTabs.Project,
          label: 'Project',
        },
        {
          value: ProjectDetailsTabs.Partner,
          label: 'Partner',
          skip: !canSeePartnersTab,
          disabled: !project?.partner,
        },
      ].filter((tab) => !tab.skip),
    [canSeePartnersTab, project?.partner],
  );

  return (
    <BlockContainer
      sx={{
        height: '100%',
        overflow: 'auto',
      }}
    >
      <Stack direction="column" spacing={1} width="100%" minHeight={0}>
        <PartnerHeader
          displayPartnerInfo={canAccessToPartnerTab}
          partner={project?.partner}
          closeDetailsPanelTooltip={CLOSE_DETAILS_PANEL_TOOLTIP}
          onClose={onClose}
        />
        <SidePanelTabs
          key={project?.addressId}
          sx={{
            marginTop: (theme) => theme.utils.important(0),
            overflow: 'auto',
          }}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabs={tabs}
          showLabel
          hasBorderTop={false}
        >
          <TabContent activeTab={activeTab} project={project} onClose={onClose} />
        </SidePanelTabs>
      </Stack>
    </BlockContainer>
  );
};
